import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { MAP_EMBED_URL } from "@/lib/mapConfig";
import { toast } from "sonner";
import { Phone, MapPin, Mail } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Thank you! We will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with PhysioAbhi Healthcare Foundation in Thalaghattapura, Bengaluru. Call 7990793367 or 7507939338, or send us an inquiry."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Reach out. We're here to help."
        subtitle="Whether it's an inquiry about our programs or a partnership idea — we'd love to hear from you."
      />
      <section className="px-5 md:px-10 py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="contact-name" className="text-sm font-medium">Name</Label>
                <Input id="contact-name" data-testid="contact-name-input" required className="rounded-none mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-sm font-medium">Email</Label>
                <Input id="contact-email" type="email" data-testid="contact-email-input" required className="rounded-none mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-phone" className="text-sm font-medium">Phone (optional)</Label>
                <Input id="contact-phone" data-testid="contact-phone-input" className="rounded-none mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-subject" className="text-sm font-medium">Subject</Label>
                <Input id="contact-subject" data-testid="contact-subject-input" className="rounded-none mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="contact-message" className="text-sm font-medium">Message</Label>
                <Textarea id="contact-message" data-testid="contact-message-input" required rows={5} className="rounded-none mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button type="submit" disabled={submitting} data-testid="contact-submit-button" size="lg" className="rounded-none bg-accent text-accent-foreground hover:opacity-90 w-full h-13">
                {submitting ? "Sending…" : "Send Inquiry"}
              </Button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-6">
              <div className="flex gap-4">
                <MapPin className="h-5 w-5 text-accent shrink-0 mt-1" />
                <p className="text-muted-foreground leading-relaxed">Ground Floor, Kamala Ark, Judicial Layout, Thalaghattapura, Kanakapura Road, Bengaluru 560109</p>
              </div>
              <div className="flex gap-4">
                <Phone className="h-5 w-5 text-accent shrink-0 mt-1" />
                <p className="text-muted-foreground">
                  <a href="tel:7990793367" data-testid="contact-phone-link-1" className="hover:text-accent">7990793367</a> {" | "}
                  <a href="tel:7507939338" data-testid="contact-phone-link-2" className="hover:text-accent">7507939338</a>
                </p>
              </div>
              <div className="flex gap-4">
                <Mail className="h-5 w-5 text-accent shrink-0 mt-1" />
                <p className="text-muted-foreground">care@physioabhi.org</p>
              </div>
              <div data-testid="contact-map" className="border border-border aspect-[4/3] overflow-hidden mt-6">
                <iframe
                  title="PhysioAbhi Location"
                  src={MAP_EMBED_URL}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
